// Read-only per-location call-flow viewer (phase 2 of the call-flows plan).
// Location list | layered SVG diagram | settings panel for the clicked node.
// The backend already filtered the list to locations this user may view.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapPin, Phone, Clock, Headphones, Voicemail, User, ExternalLink, PhoneOff, ListTree, Plus, Pencil, Upload, Download, ZoomIn, ZoomOut, Maximize2, Network, List } from 'lucide-react';
import { api, CallFlow, CallFlowNode, QueueFlowNode } from '../api/client';
import { useApi } from '../hooks/useApi';
import { exportSvgAsPng } from '../lib/download';

export interface DialTarget {
  id: string;
  type: 'user' | 'queue' | 'contact' | 'personal';
  displayName: string;
  email?: string;
  phoneNumber?: string;
}

interface CallFlowsViewerProps {
  flows: CallFlow[];
  accentColor: string;
  dirById: Record<string, DialTarget>;
  onDial: (item: DialTarget) => void;
  /** Admin affordances (phase 3): add/edit buttons appear when provided. */
  isAdmin?: boolean;
  onAddLocation?: () => void;
  onEditLocation?: (flow: CallFlow) => void;
  onImport?: () => void;
  /** Optionally controlled by the host (CallFlowsTab) so switching between the
   *  guide and the diagram keeps the selected location and view. When `mode`
   *  is controlled, the internal Diagram/Outline toggle is hidden. */
  mode?: 'diagram' | 'outline';
  onModeChange?: (mode: 'diagram' | 'outline') => void;
  locationId?: string | null;
  onSelectLocation?: (id: string) => void;
}

// ---------- Layout ----------

const CANVAS_W = 660;
const NODE_W = 168;

// Width we reserve (and draw the white backing rect) for an edge label. Used
// both when sizing the edge for dagre and when rendering, so they stay in sync.
const edgeLabelW = (s?: string) => (s ? Math.min(132, 10 + s.length * 5.4) : 0);

// Smooth a dagre-routed polyline into a Catmull-Rom spline. Curved edges read as
// distinct strands where straight diagonals would blur into a hatch — the main
// reason a dense fan-out looks tidy instead of tangled.
function splinePath(pts: Array<{ x: number; y: number }>): string {
  if (pts.length < 2) return '';
  if (pts.length === 2) return `M${pts[0].x},${pts[0].y} L${pts[1].x},${pts[1].y}`;
  let d = `M${pts[0].x},${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const c1x = p1.x + (p2.x - p0.x) / 6, c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6, c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C${c1x},${c1y} ${c2x},${c2y} ${p2.x},${p2.y}`;
  }
  return d;
}

interface PNode {
  id: string;
  kind: 'entry' | 'schedule' | CallFlowNode['type'];
  title: string;
  sub: string;
  x: number; // center
  y: number; // center
  h: number;
  ref?: CallFlowNode;
  /** Set on a duplicated terminal copy — the real node id to select on click. */
  selectId?: string;
  /** Direct dial-in number to show on the box, if the node has one. */
  phone?: string;
  /** Has downstream nodes that can be folded away. */
  collapsible?: boolean;
  /** Currently folded — its unique subtree is hidden. */
  collapsed?: boolean;
  /** How many direct branches are hidden beneath a collapsed node. */
  hiddenCount?: number;
}

interface PEdge {
  from: string;
  to: string;
  label?: string;
  dashed?: boolean;
  labelColor?: string;
  menu?: boolean; // an auto-attendant menu option (rendered "Option N" beside the line)
  points?: Array<{ x: number; y: number }>; // dagre-routed polyline (in viewBox units)
  lx?: number; // dagre-chosen label center
  ly?: number;
}

const ROUTING_LABEL: Record<string, string> = {
  roundRobin: 'round robin',
  longestIdle: 'longest idle',
  attendant: 'attendant',
  serial: 'serial',
};

function nodeSub(n: CallFlowNode): string {
  switch (n.type) {
    case 'autoAttendant':
      return `${n.menu.length} option${n.menu.length === 1 ? '' : 's'} · dial-by-name ${n.dialByName ? 'on' : 'off'}`;
    case 'queue': {
      const bits = [n.routing ? ROUTING_LABEL[n.routing] : null, n.agentsGroupName || null].filter(Boolean);
      return bits.join(' · ') || 'call queue';
    }
    default:
      return n.detail || '';
  }
}

// Merge parallel edges (same from→to) into one, keeping DISTINCT labels so a
// shared target's labels neither overprint nor repeat. Run again after any edge
// rewiring (e.g. the AA merge below) that can create new parallels.
function mergeParallel(list: PEdge[]): PEdge[] {
  const m = new Map<string, PEdge & { _labels: string[] }>();
  for (const e of list) {
    const k = `${e.from}->${e.to}`;
    const ex = m.get(k);
    if (!ex) {
      m.set(k, { ...e, _labels: e.label ? [e.label] : [] });
    } else {
      if (e.label && !ex._labels.includes(e.label)) ex._labels.push(e.label);
      ex.dashed = !!(ex.dashed && e.dashed);
      ex.labelColor = ex.labelColor || e.labelColor;
      ex.menu = ex.menu || e.menu;
    }
  }
  return [...m.values()].map(({ _labels, ...e }) => ({ ...e, label: _labels.join(' · ') || undefined }));
}

function commonPrefix(strs: string[]): string {
  if (!strs.length) return '';
  let p = strs[0];
  for (const s of strs) while (p && !s.startsWith(p)) p = p.slice(0, -1);
  return p;
}

// Collapse structurally-identical sibling auto attendants — same parents AND the
// same set of outgoing targets — into a single box. The common case is one menu
// duplicated per language (Dutch/French/…) that all route to the same
// departments: drawing it once removes most of the converging lines in a busy
// flow. A genuinely different menu (an extra or missing option) has a different
// signature and stays separate, so the merge never hides a real difference. The
// merged box keeps selectId so a click still opens a representative node.
function aaMerge(nodes: PNode[], edges: PEdge[]): { nodes: PNode[]; edges: PEdge[] } {
  const childrenOf = new Map<string, Set<string>>();
  const parentsOf = new Map<string, Set<string>>();
  for (const e of edges) {
    if (!childrenOf.has(e.from)) childrenOf.set(e.from, new Set());
    childrenOf.get(e.from)!.add(e.to);
    if (!parentsOf.has(e.to)) parentsOf.set(e.to, new Set());
    parentsOf.get(e.to)!.add(e.from);
  }
  const sig = (id: string) =>
    [...(childrenOf.get(id) ?? [])].sort().join(',') + '||' + [...(parentsOf.get(id) ?? [])].sort().join(',');

  const groups = new Map<string, PNode[]>();
  for (const n of nodes) {
    if (n.kind !== 'autoAttendant') continue;
    const k = sig(n.id);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k)!.push(n);
  }

  const remap = new Map<string, string>();
  const mergedNodes: PNode[] = [];
  const dropped = new Set<string>();
  for (const grp of groups.values()) {
    if (grp.length < 2) continue;
    const rep = grp[0];
    const mid = `__merge-${rep.id}`;
    const prefix = commonPrefix(grp.map(n => n.title));
    const variants = grp
      .map(n => n.title.slice(prefix.length).replace(/^[\s·)(-]+/, '').split(/[\s(]/)[0])
      .filter(Boolean);
    const title = prefix.replace(/[\s·-]+$/, '').trim() || rep.title;
    grp.forEach(n => { remap.set(n.id, mid); dropped.add(n.id); });
    mergedNodes.push({ ...rep, id: mid, title, sub: `${grp.length} menus · ${variants.join(' · ')}`, selectId: rep.id });
  }
  if (!remap.size) return { nodes, edges };

  const outNodes = nodes.filter(n => !dropped.has(n.id)).concat(mergedNodes);
  const outEdges = edges
    .map(e => ({ ...e, from: remap.get(e.from) ?? e.from, to: remap.get(e.to) ?? e.to }))
    .filter(e => e.from !== e.to); // a collapsed group never points within itself, but guard anyway
  return { nodes: outNodes, edges: outEdges };
}

// Build the diagram layout with dagre (layered DAG auto-layout). We hand dagre
// the node boxes plus a deduped, label-baked edge list, and read back node
// centers, routed edge polylines, and label positions. dagre handles the hard
// parts the old hand-rolled layering couldn't: multi-parent convergence, cycles
// (a menu option looping back), and crossing minimisation. It's lazy-loaded by
// the parent and passed in so it never lands in the main bundle.
function buildLayout(flow: CallFlow, dagre: any, numberById?: Record<string, string>, collapsed?: Set<string>): { nodes: PNode[]; edges: PEdge[]; width: number; height: number } {
  // Nodes carrying a dial-in number are one text line taller to fit it.
  const heightOf = (kind: PNode['kind'], hasPhone: boolean) =>
    (kind === 'entry' ? 52 : kind === 'schedule' ? 48 : kind === 'queue' ? 56 : 50) + (hasPhone ? 13 : 0);

  // ---- nodes: synthetic entry + schedule, then every flow node ----
  let nodes: PNode[] = [];
  const extra = flow.mainNumbers.length > 1 ? ` +${flow.mainNumbers.length - 1}` : '';
  nodes.push({ id: '__entry', kind: 'entry', title: (flow.mainNumbers[0] || 'Main number') + extra, sub: `Main number · ${flow.locationName}`, x: 0, y: 0, h: 52 });
  const first = flow.schedule.hours[0];
  nodes.push({ id: '__schedule', kind: 'schedule', title: 'Business hours?', sub: first ? `${first.days} ${first.open}–${first.close}${flow.timezone ? ' · ' + flow.timezone : ''}` : 'no hours defined', x: 0, y: 0, h: 48 });
  for (const n of flow.nodes) {
    const phone = numberById?.[n.id];
    nodes.push({ id: n.id, kind: n.type, title: n.label, sub: nodeSub(n), x: 0, y: 0, h: heightOf(n.type, !!phone), ref: n, phone });
  }
  const ids = new Set(nodes.map(n => n.id));

  // ---- edges: collect (label baked in), then merge parallels so a shared
  // target's labels don't overprint (e.g. a menu key and 'operator' to one node).
  const raw: PEdge[] = [];
  const add = (from: string, to: string | undefined, label?: string, dashed?: boolean, labelColor?: string, menu?: boolean) => {
    if (!to || !ids.has(to) || !ids.has(from)) return;
    raw.push({ from, to, label, dashed, labelColor, menu });
  };
  const menuLabel = (key: string) => (/^[0-9*#]$/.test(key) ? `Option ${key}` : key === 'auto' ? '' : key);
  add('__entry', '__schedule');
  add('__schedule', flow.openTargetId, 'open', false, '#16a34a');
  add('__schedule', flow.closedTargetId, 'closed', true);
  for (const n of flow.nodes) {
    if (n.type === 'autoAttendant') {
      for (const m of n.menu) add(n.id, m.targetId, menuLabel(m.key), false, undefined, true);
      add(n.id, n.operatorTargetId, 'operator');
    } else if (n.type === 'queue') {
      add(n.id, n.overflowTargetId, `overflow${n.overflowThreshold !== undefined ? ` ${n.overflowThreshold}` : ''}`, true);
      add(n.id, n.timeoutTargetId, `timeout${n.timeoutSeconds !== undefined ? ` ${n.timeoutSeconds}s` : ''}`, true);
    }
  }
  let edges = mergeParallel(raw);

  // Collapse identical sibling menus (e.g. per-language copies), then re-merge
  // the parallels that collapsing the duplicates created.
  ({ nodes, edges } = aaMerge(nodes, edges));
  edges = mergeParallel(edges);

  // ---- fold away the subtrees of any collapsed nodes ----
  // BFS from entry but don't descend into collapsed nodes; anything no longer
  // reached (i.e. only reachable through a collapsed node) is hidden. A node
  // shared with an expanded branch stays visible — correct, not hidden.
  {
    const childIds = new Map<string, string[]>();
    for (const e of edges) {
      if (!childIds.has(e.from)) childIds.set(e.from, []);
      childIds.get(e.from)!.push(e.to);
    }
    const visible = new Set<string>(['__entry']);
    const q = ['__entry'];
    while (q.length) {
      const id = q.shift()!;
      if (collapsed?.has(id)) continue;
      for (const c of childIds.get(id) ?? []) if (!visible.has(c)) { visible.add(c); q.push(c); }
    }
    for (const n of nodes) {
      const kids = childIds.get(n.id) ?? [];
      n.collapsible = kids.length > 0 && n.kind !== 'entry' && n.kind !== 'schedule';
      if (collapsed?.has(n.id)) { n.collapsed = true; n.hiddenCount = kids.length; }
    }
    nodes = nodes.filter(n => visible.has(n.id));
    // Drop a collapsed node's OWN outgoing edges too — so folding a branch always
    // hides its fan-out lines, even when the targets stay visible because another
    // (expanded) branch still reaches them. Targets reachable only through the
    // collapsed node are already gone (BFS above didn't descend into it).
    edges = edges.filter(e => visible.has(e.from) && visible.has(e.to) && !collapsed?.has(e.from));
  }

  // ---- duplicate pure terminal sinks (voicemail/disconnect/external) so each
  // caller gets its OWN copy directly beneath it, instead of many long lines
  // converging on one shared box (the bottom of a busy flow turns into a knot
  // otherwise). Real structural nodes (AAs, queues, people) stay shared. Each
  // copy keeps selectId so a click still opens the original node's settings.
  const TERMINAL_DUP = new Set<PNode['kind']>(['voicemail', 'disconnect', 'external']);
  const incoming = new Map<string, PEdge[]>();
  for (const e of edges) {
    const arr = incoming.get(e.to);
    if (arr) arr.push(e); else incoming.set(e.to, [e]);
  }
  const lnodes: PNode[] = [];
  for (const n of nodes) {
    const inc = incoming.get(n.id) ?? [];
    if (TERMINAL_DUP.has(n.kind) && inc.length > 1) {
      inc.forEach((e, k) => {
        lnodes.push({ ...n, id: `${n.id}~${k}`, selectId: n.id });
        e.to = `${n.id}~${k}`; // repoint each incoming edge to its own copy
      });
    } else {
      lnodes.push(n);
    }
  }

  // ---- run dagre (top-to-bottom layered layout) ----
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: 'TB', nodesep: 22, ranksep: 58, edgesep: 10, marginx: 16, marginy: 16 });
  g.setDefaultEdgeLabel(() => ({}));
  for (const n of lnodes) g.setNode(n.id, { width: NODE_W, height: n.h });
  for (const e of edges) {
    const w = edgeLabelW(e.label);
    g.setEdge(e.from, e.to, w ? { width: w, height: 12, labelpos: 'c' } : {});
  }
  dagre.layout(g);

  for (const n of lnodes) {
    const gn = g.node(n.id);
    if (gn) { n.x = gn.x; n.y = gn.y; }
  }
  for (const e of edges) {
    const ge = g.edge(e.from, e.to);
    if (ge) { e.points = ge.points; e.lx = ge.x; e.ly = ge.y; }
  }
  const gg = g.graph();
  return { nodes: lnodes, edges, width: Math.max(CANVAS_W, Math.ceil(gg.width || CANVAS_W)), height: Math.ceil(gg.height || 200) };
}

export type FlowLayout = { nodes: PNode[]; edges: PEdge[]; width: number; height: number };

// Lazy-load dagre as its own chunk and compute the layout for a flow. `layout`
// is null until the engine has loaded; `failed` flips true if the chunk can't be
// fetched (e.g. a stale hashed chunk 404s after a redeploy) so callers can offer
// a way out instead of a forever-spinner. Shared by the viewer and the editor's
// live preview so the load logic lives in one place.
// eslint-disable-next-line react-refresh/only-export-components -- shared hook deliberately co-located with the diagram components it pairs with
export function useFlowLayout(flow: CallFlow | null | undefined, numberById?: Record<string, string>, collapsed?: Set<string>): { layout: FlowLayout | null; failed: boolean } {
  const [dagre, setDagre] = useState<any>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    import('@dagrejs/dagre')
      .then(m => { const d: any = (m as any).default ?? m; if (live) setDagre(d.graphlib ? d : m); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, []);
  const layout = useMemo(() => (dagre && flow ? buildLayout(flow, dagre, numberById, collapsed) : null), [dagre, flow, numberById, collapsed]);
  return { layout, failed };
}

// ---------- Rendering ----------

function nodeStyle(kind: PNode['kind'], accent: string): { fill: string; stroke: string; titleFill: string; subFill: string; dashed?: boolean } {
  switch (kind) {
    case 'entry': return { fill: accent, stroke: accent, titleFill: '#ffffff', subFill: 'rgba(255,255,255,0.75)' };
    case 'schedule': return { fill: '#ffffff', stroke: '#d1d5db', titleFill: '#374151', subFill: '#6b7280' };
    case 'autoAttendant': return { fill: '#ffffff', stroke: '#a78bfa', titleFill: '#5b21b6', subFill: '#8b7aa8' };
    case 'queue': return { fill: '#f0fdfa', stroke: '#14b8a6', titleFill: '#0f766e', subFill: '#0d9488' };
    case 'disconnect': return { fill: '#ffffff', stroke: '#d1d5db', titleFill: '#6b7280', subFill: '#9ca3af', dashed: true };
    default: return { fill: '#ffffff', stroke: '#d1d5db', titleFill: '#374151', subFill: '#6b7280' };
  }
}

const NODE_ICON: Record<string, typeof Phone> = {
  entry: Phone, schedule: Clock, autoAttendant: ListTree, queue: Headphones,
  voicemail: Voicemail, person: User, external: ExternalLink, disconnect: PhoneOff,
};

// Menu fan-out edges are colored by their source so a strand can be traced back
// to its menu through the crossings in a busy flow. Spine + terminal edges stay
// neutral gray. Distinct, reasonably colour-blind-distinguishable hues.
const EDGE_PALETTE = ['#7c3aed', '#0ea5e9', '#16a34a', '#ea580c', '#db2777', '#0d9488', '#ca8a04', '#4f46e5'];

export function FlowDiagram({ layout, accent, selectedId, onSelect, zoom, onToggleCollapse }: {
  layout: { nodes: PNode[]; edges: PEdge[]; width: number; height: number };
  accent: string; selectedId: string | null; onSelect: (id: string) => void; zoom?: number;
  /** When provided, collapsible nodes get a fold/unfold toggle. */
  onToggleCollapse?: (id: string) => void;
}) {
  const { nodes, edges, width, height } = layout;
  // One palette colour per menu source, assigned in first-seen order.
  const srcColor = useMemo(() => {
    const m = new Map<string, string>();
    let i = 0;
    for (const e of edges) if (e.menu && !m.has(e.from)) m.set(e.from, EDGE_PALETTE[i++ % EDGE_PALETTE.length]);
    return m;
  }, [edges]);

  return (
    <svg
      width={zoom !== undefined ? Math.round(width * zoom) : '100%'}
      height={zoom !== undefined ? Math.round(height * zoom) : undefined}
      style={zoom === undefined && width > CANVAS_W ? { minWidth: width } : undefined}
      fontFamily="'Segoe UI', system-ui, -apple-system, sans-serif"
      viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Call flow diagram">
      <defs>
        <marker id="cf-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M2 1L8 5L2 9" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" />
        </marker>
      </defs>
      {edges.map((e, i) => {
        if (!e.points || e.points.length < 2) return null;
        // dagre routes each edge around the nodes; we smooth those points into a
        // spline so a dense fan-out reads as separate strands, not a hatch.
        const d = splinePath(e.points);
        const w = edgeLabelW(e.label);
        const col = (e.menu && srcColor.get(e.from)) || '#9ca3af';
        return (
          <g key={i}>
            <path d={d} fill="none" stroke={col} strokeWidth="1.2" strokeOpacity="0.85" strokeDasharray={e.dashed ? '3 3' : undefined} markerEnd="url(#cf-arrow)" />
            {e.label && e.lx !== undefined && e.ly !== undefined && (
              <>
                <rect x={e.lx - w / 2} y={e.ly - 7} width={w} height={13} rx="2" fill="#ffffff" opacity="0.82" />
                <text x={e.lx} y={e.ly + 3} textAnchor="middle" fontSize="9" fontWeight="600" fill={e.labelColor || col}>{e.label}</text>
              </>
            )}
          </g>
        );
      })}
      {nodes.map(n => {
        const st = nodeStyle(n.kind, accent);
        const selectId = n.selectId ?? n.id;
        const selected = selectId === selectedId;
        const Icon = NODE_ICON[n.kind] || Phone;
        return (
          <g key={n.id} onClick={() => onSelect(selectId)} style={{ cursor: 'pointer' }}>
            <rect
              x={n.x - NODE_W / 2} y={n.y - n.h / 2} width={NODE_W} height={n.h}
              rx={n.kind === 'schedule' ? n.h / 2 : 6}
              fill={st.fill} stroke={selected ? accent : st.stroke} strokeWidth={selected ? 2 : 1.2}
              strokeDasharray={st.dashed ? '4 3' : undefined}
            />
            <text x={n.x} y={n.phone ? n.y - 10 : n.y - 3} textAnchor="middle" fontSize="10.5" fontWeight="600" fill={st.titleFill}>
              {n.title.length > 30 ? n.title.slice(0, 29) + '…' : n.title}
            </text>
            <text x={n.x} y={n.phone ? n.y + 3 : n.y + 11} textAnchor="middle" fontSize="8.5" fill={st.subFill}>
              {n.sub.length > 40 ? n.sub.slice(0, 39) + '…' : n.sub}
            </text>
            {n.phone && (
              <text x={n.x} y={n.y + 16} textAnchor="middle" fontSize="9" fontWeight="700" fill={accent}>☎ {n.phone}</text>
            )}
            <Icon x={n.x - NODE_W / 2 + 8} y={n.y - 6} width={12} height={12} color={st.titleFill} />
            {n.collapsible && onToggleCollapse && (
              <g onClick={e => { e.stopPropagation(); onToggleCollapse(n.id); }} style={{ cursor: 'pointer' }}>
                <rect
                  x={n.x - (n.collapsed ? 11 : 8)} y={n.y + n.h / 2 - 7}
                  width={n.collapsed ? 22 : 16} height={14} rx={7}
                  fill="#ffffff" stroke={st.stroke} strokeWidth={1}
                />
                <text x={n.x} y={n.y + n.h / 2 + 3} textAnchor="middle" fontSize="9" fontWeight="700" fill={st.titleFill}>
                  {n.collapsed ? `+${n.hiddenCount ?? 0}` : '–'}
                </text>
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ---------- Outline view (scales better than the diagram for big flows) ----------

function OutlineLine({ edgeLabel, Icon, title, sub, muted }: {
  edgeLabel?: string; Icon: typeof Phone; title: string; sub?: string; muted?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 py-1 align-middle">
      {edgeLabel && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-medium shrink-0">{edgeLabel}</span>
      )}
      <Icon className="w-3.5 h-3.5 text-gray-500 shrink-0" />
      <span className={`text-sm ${muted ? 'text-gray-400 italic' : 'text-gray-900 font-medium'}`}>{title}</span>
      {sub && <span className="text-xs text-gray-400">· {sub}</span>}
    </span>
  );
}

function OutlineView({ flow, dirById, onDial, accentColor }: {
  flow: CallFlow; dirById: Record<string, DialTarget>; onDial: (i: DialTarget) => void; accentColor: string;
}) {
  const byId = useMemo(() => new Map(flow.nodes.map(n => [n.id, n])), [flow]);
  // Expanded once, referenced elsewhere — mirrors the diagram's cycle handling.
  const rendered = new Set<string>();
  const indent = 'ml-3 border-l border-gray-200 pl-3';

  // Per-node specifics that aren't a routing target (greeting, number, agents +
  // members). Rendered inside the node's <details> above its child targets.
  function renderDetail(n: CallFlowNode): React.ReactElement | null {
    if (n.type === 'autoAttendant') {
      return n.greeting
        ? <div className="text-xs text-gray-400 italic py-0.5">“{n.greeting.length > 120 ? n.greeting.slice(0, 119) + '…' : n.greeting}”</div>
        : null;
    }
    if (n.type === 'queue') {
      const num = n.directoryQueueId ? dirById[n.directoryQueueId]?.phoneNumber : undefined;
      const rows: React.ReactElement[] = [];
      if (num) rows.push(<div key="num" className="text-xs text-gray-500">Number: {num}</div>);
      if (n.agentsGroupName) rows.push(<div key="grp" className="text-xs text-gray-500">Agents: {n.agentsGroupName}</div>);
      if (n.agentsGroupId) rows.push(<LazyMembers key="mem" groupId={n.agentsGroupId} accentColor={accentColor} onDial={onDial} />);
      return rows.length ? <div className="py-0.5 space-y-0.5">{rows}</div> : null;
    }
    return null; // terminals: their detail is already in the sub line
  }

  function renderTarget(id: string | undefined, edgeLabel: string, key: string): React.ReactElement {
    if (!id || !byId.has(id)) {
      return <div key={key}><OutlineLine edgeLabel={edgeLabel} Icon={PhoneOff} title="(missing target)" muted /></div>;
    }
    const n = byId.get(id)!;
    const Icon = NODE_ICON[n.type] || Phone;
    if (rendered.has(id)) {
      return <div key={key}><OutlineLine edgeLabel={edgeLabel} Icon={Icon} title={n.label} sub="shown above" muted /></div>;
    }
    rendered.add(id);
    const kids: Array<{ id?: string; label: string }> = [];
    if (n.type === 'autoAttendant') {
      n.menu.forEach(m => kids.push({ id: m.targetId, label: `Option ${m.key}` }));
      if (n.operatorTargetId) kids.push({ id: n.operatorTargetId, label: 'operator' });
    } else if (n.type === 'queue') {
      if (n.overflowTargetId) kids.push({ id: n.overflowTargetId, label: `overflow${n.overflowThreshold !== undefined ? ` ${n.overflowThreshold} calls` : ''}` });
      if (n.timeoutTargetId) kids.push({ id: n.timeoutTargetId, label: `timeout${n.timeoutSeconds !== undefined ? ` ${n.timeoutSeconds}s` : ''}` });
    }
    const detail = renderDetail(n);
    if (kids.length === 0 && !detail) {
      return <div key={key}><OutlineLine edgeLabel={edgeLabel} Icon={Icon} title={n.label} sub={nodeSub(n)} /></div>;
    }
    return (
      <details key={key} open>
        <summary className="cursor-pointer"><OutlineLine edgeLabel={edgeLabel} Icon={Icon} title={n.label} sub={nodeSub(n)} /></summary>
        <div className={indent}>
          {detail}
          {kids.map((k, i) => renderTarget(k.id, k.label, `${id}>${i}`))}
        </div>
      </details>
    );
  }

  const first = flow.schedule.hours[0];
  const hoursLabel = first ? `${first.days} ${first.open}–${first.close}` : 'no hours defined';

  return (
    <div className="text-sm">
      <div><OutlineLine Icon={Phone} title={flow.mainNumbers[0] || 'Main number'} sub={`Main number · ${flow.locationName}`} /></div>
      <div className={indent}>
        <details open>
          <summary className="cursor-pointer"><OutlineLine Icon={Clock} title="Business hours?" sub={hoursLabel} /></summary>
          <div className={indent}>
            {renderTarget(flow.openTargetId, 'open', 'open')}
            {renderTarget(flow.closedTargetId, 'closed', 'closed')}
          </div>
        </details>
      </div>
    </div>
  );
}

// ---------- Settings panel ----------

// Members of a queue's agent group, with numbers + click-to-call. Fetches on
// mount, so callers render it only when the members should actually load
// (directly in the panel; via LazyMembers behind a toggle in the outline).
function QueueAgents({ groupId, accentColor, onDial }: {
  groupId: string; accentColor: string; onDial: (i: DialTarget) => void;
}) {
  const { data: members, loading, error } = useApi(() => api.getGroupMembers(groupId), { deps: [groupId] });
  if (loading) return <div className="text-xs text-gray-400 py-1">Loading members…</div>;
  if (error) return <div className="text-xs text-red-500 py-1">Couldn't load members</div>;
  if (!members || members.length === 0) return <div className="text-xs text-gray-400 py-1">No members</div>;
  return (
    <div className="mt-1 border border-gray-100 rounded divide-y divide-gray-100 max-h-56 overflow-y-auto">
      {members.map(m => (
        <button key={m.id}
          onClick={() => onDial({ id: m.id, type: 'user', displayName: m.displayName, email: m.email, phoneNumber: m.phoneNumber })}
          className="w-full px-2 py-1.5 flex items-center gap-2 text-left hover:bg-gray-50">
          <span className="flex-1 min-w-0 truncate text-xs text-gray-800">{m.displayName}</span>
          {m.phoneNumber && <span className="text-[11px] text-gray-500 font-mono shrink-0">{m.phoneNumber}</span>}
          <Phone className="w-3 h-3 shrink-0" style={{ color: accentColor }} />
        </button>
      ))}
    </div>
  );
}

// Collapsed members list that only fetches once expanded — for the outline,
// where auto-loading every queue's group would be a Graph-call storm.
function LazyMembers({ groupId, accentColor, onDial }: {
  groupId: string; accentColor: string; onDial: (i: DialTarget) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <details onToggle={e => setOpen((e.currentTarget as HTMLDetailsElement).open)}>
      <summary className="cursor-pointer text-xs text-gray-500">Members</summary>
      {open && <QueueAgents groupId={groupId} accentColor={accentColor} onDial={onDial} />}
    </details>
  );
}

function SettingRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="text-xs">
      <div className="text-gray-400">{label}</div>
      <div className="text-gray-800">{children}</div>
    </div>
  );
}

function NodeSettings({ flow, nodeId, dirById, onDial, accentColor }: {
  flow: CallFlow; nodeId: string; dirById: Record<string, DialTarget>; onDial: (i: DialTarget) => void; accentColor: string;
}) {
  const labelOf = (id?: string) => flow.nodes.find(n => n.id === id)?.label || '—';

  if (nodeId === '__entry') {
    return (
      <div className="space-y-2.5">
        <SettingRow label="Location">{flow.locationName}</SettingRow>
        <SettingRow label="Main numbers">{flow.mainNumbers.join(', ')}</SettingRow>
        {flow.timezone && <SettingRow label="Timezone">{flow.timezone}</SettingRow>}
        {flow.updatedAt && <SettingRow label="Last updated">{flow.updatedAt.slice(0, 10)}{flow.updatedBy ? ` by ${flow.updatedBy}` : ''}</SettingRow>}
      </div>
    );
  }
  if (nodeId === '__schedule') {
    return (
      <div className="space-y-2.5">
        <SettingRow label="Business hours">
          {flow.schedule.hours.length === 0 ? '—' : flow.schedule.hours.map((h, i) => (
            <div key={i}>{h.days} {h.open}–{h.close}</div>
          ))}
        </SettingRow>
        {flow.timezone && <SettingRow label="Timezone">{flow.timezone}</SettingRow>}
        <SettingRow label="Holidays">
          {flow.schedule.holidays.length === 0 ? 'none' : flow.schedule.holidays.map((h, i) => (
            <div key={i}>{h.name}{h.date ? ` (${h.date})` : ''}</div>
          ))}
        </SettingRow>
        <SettingRow label="When open">{labelOf(flow.openTargetId)}</SettingRow>
        <SettingRow label="When closed">{labelOf(flow.closedTargetId)}</SettingRow>
      </div>
    );
  }

  const node = flow.nodes.find(n => n.id === nodeId);
  if (!node) return <div className="text-xs text-gray-400">Select a node in the diagram.</div>;

  if (node.type === 'autoAttendant') {
    return (
      <div className="space-y-2.5">
        {node.phoneNumber && <SettingRow label="Number">{node.phoneNumber}</SettingRow>}
        {node.greeting && <SettingRow label="Greeting">{node.greeting}</SettingRow>}
        {node.language && <SettingRow label="Language">{node.language}</SettingRow>}
        <SettingRow label="Dial by name">{node.dialByName ? 'enabled' : 'disabled'}</SettingRow>
        <SettingRow label="Menu">
          {node.menu.map((m, i) => <div key={i}>{m.key} — {m.label} → {labelOf(m.targetId)}</div>)}
        </SettingRow>
        {node.operatorTargetId && <SettingRow label="Operator">→ {labelOf(node.operatorTargetId)}</SettingRow>}
      </div>
    );
  }
  if (node.type === 'queue') {
    const q = node as QueueFlowNode;
    const dirItem = q.directoryQueueId ? dirById[q.directoryQueueId] : undefined;
    const queueNumber = q.phoneNumber || dirItem?.phoneNumber;
    return (
      <div className="space-y-2.5">
        {queueNumber && <SettingRow label="Number">{queueNumber}</SettingRow>}
        {q.routing && <SettingRow label="Routing">{ROUTING_LABEL[q.routing]}</SettingRow>}
        {q.agentsGroupName && <SettingRow label="Agents">{q.agentsGroupName}</SettingRow>}
        {q.agentsGroupId && (
          <div className="text-xs">
            <div className="text-gray-400">Members</div>
            <QueueAgents groupId={q.agentsGroupId} accentColor={accentColor} onDial={onDial} />
          </div>
        )}
        {q.overflowThreshold !== undefined && (
          <SettingRow label="Overflow">{q.overflowThreshold} calls → {labelOf(q.overflowTargetId)}</SettingRow>
        )}
        {q.timeoutSeconds !== undefined && (
          <SettingRow label="Timeout">{q.timeoutSeconds}s → {labelOf(q.timeoutTargetId)}</SettingRow>
        )}
        {dirItem && (
          <button
            onClick={() => onDial(dirItem)}
            className="mt-1 w-full px-3 py-1.5 text-xs text-white rounded font-medium flex items-center justify-center gap-1.5 hover:opacity-90"
            style={{ backgroundColor: accentColor }}
          >
            <Phone className="w-3.5 h-3.5" />Call {dirItem.displayName}
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="space-y-2.5">
      <SettingRow label="Type">{node.type}</SettingRow>
      {node.detail && <SettingRow label="Detail">{node.detail}</SettingRow>}
    </div>
  );
}

// ---------- Main component ----------

export function CallFlowsViewer({
  flows, accentColor, dirById, onDial, isAdmin, onAddLocation, onEditLocation, onImport,
  mode: modeProp, onModeChange, locationId: locationProp, onSelectLocation,
}: CallFlowsViewerProps) {
  const [locationState, setLocationState] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<string>('__entry');
  const [exportState, setExportState] = useState<'idle' | 'busy' | 'failed'>('idle');
  const [modeState, setModeState] = useState<'diagram' | 'outline'>('diagram');
  const modeControlled = modeProp !== undefined;
  const mode = modeProp ?? modeState;
  const setMode = (m: 'diagram' | 'outline') => { if (onModeChange) onModeChange(m); if (!modeControlled) setModeState(m); };
  const locationId = locationProp !== undefined ? locationProp : locationState;
  const setLocationId = (id: string) => { if (onSelectLocation) onSelectLocation(id); if (locationProp === undefined) setLocationState(id); };
  const [zoom, setZoom] = useState(1);
  const diagramRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const flow = flows.find(f => f.id === locationId) || flows[0];
  // Dial-in number per node: the one captured at import (node.phoneNumber),
  // falling back to the directory for queues (their resource-account number).
  const numberById = useMemo<Record<string, string>>(() => {
    const m: Record<string, string> = {};
    for (const n of flow?.nodes || []) {
      const direct = (n as { phoneNumber?: string }).phoneNumber;
      const viaDir = n.type === 'queue' && n.directoryQueueId ? dirById[n.directoryQueueId]?.phoneNumber : undefined;
      const num = direct || viaDir;
      if (num) m[n.id] = num;
    }
    return m;
  }, [flow, dirById]);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const onToggleCollapse = useCallback((id: string) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const { layout, failed: layoutFailed } = useFlowLayout(flow, numberById, collapsed);

  const computeFit = useCallback(() => {
    const w = layout?.width ?? 0;
    const cw = scrollRef.current?.clientWidth ?? 0;
    // Only ever shrink to fit (cap at 1.0); small flows stay at 100%.
    if (w > 0 && cw > 0) return Math.max(0.3, Math.min(1, (cw - 24) / w));
    return 1;
  }, [layout]);
  // Auto-fit only when the LOCATION or view mode changes — not on every layout
  // change, so collapsing/expanding a branch keeps the user's current zoom.
  const fitPendingRef = useRef(true);
  useEffect(() => { fitPendingRef.current = true; }, [flow?.id, mode]);
  useEffect(() => {
    if (mode !== 'diagram' || !layout || !fitPendingRef.current) return;
    fitPendingRef.current = false;
    const id = requestAnimationFrame(() => setZoom(computeFit()));
    return () => cancelAnimationFrame(id);
  }, [layout, mode, computeFit]);

  if (!flow) return null;

  async function exportPng() {
    if (!diagramRef.current || !flow || !layout) return;
    setExportState('busy');
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      await exportSvgAsPng(
        diagramRef.current,
        `callflow-${flow.id}-${stamp}.png`,
        `${flow.locationName} — call flow (${flow.mainNumbers[0] || ''}, exported ${stamp})`,
      );
      setExportState('idle');
    } catch {
      setExportState('failed');
      setTimeout(() => setExportState('idle'), 2500);
    }
  }

  const selectedKind = selectedNode === '__entry' ? 'Location'
    : selectedNode === '__schedule' ? 'Schedule'
    : flow.nodes.find(n => n.id === selectedNode)?.type || '';

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden grid md:grid-cols-[170px_minmax(0,1fr)_220px]">
      <div className="border-r border-gray-200 bg-gray-50/50">
        <div className="px-3 py-2.5 border-b border-gray-200 bg-white flex items-center justify-between">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Locations</span>
          <span className="flex items-center gap-1">
            {isAdmin && onImport && (
              <button onClick={onImport} title="Import from Teams"
                className="p-0.5 text-purple-600 hover:text-purple-800"><Upload className="w-4 h-4" /></button>
            )}
            {isAdmin && onAddLocation && (
              <button onClick={onAddLocation} title="Add location"
                className="p-0.5 text-purple-600 hover:text-purple-800"><Plus className="w-4 h-4" /></button>
            )}
          </span>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {flows.map(f => {
            const active = f.id === flow.id;
            return (
              <button key={f.id}
                onClick={() => { setLocationId(f.id); setSelectedNode('__entry'); setCollapsed(new Set()); }}
                className={`w-full text-left px-3 py-2.5 border-b border-gray-100 flex items-start gap-2 ${active ? 'bg-purple-50 border-l-2 border-l-purple-600' : 'hover:bg-white'}`}>
                <MapPin className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${active ? 'text-purple-600' : 'text-gray-400'}`} />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{f.locationName}</div>
                  <div className="text-xs text-gray-500 truncate">{f.mainNumbers[0] || ''}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="min-w-0">
        <div className="flex items-center justify-end gap-1.5 px-2 py-1.5 border-b border-gray-100">
          {!modeControlled && <div className="inline-flex rounded border border-gray-200 overflow-hidden mr-1">
            <button onClick={() => setMode('diagram')}
              className={`px-2 py-1 text-[11px] inline-flex items-center gap-1 ${mode === 'diagram' ? 'bg-gray-100 text-gray-800' : 'text-gray-500 hover:bg-gray-50'}`}>
              <Network className="w-3 h-3" />Diagram
            </button>
            <button onClick={() => setMode('outline')}
              className={`px-2 py-1 text-[11px] inline-flex items-center gap-1 border-l border-gray-200 ${mode === 'outline' ? 'bg-gray-100 text-gray-800' : 'text-gray-500 hover:bg-gray-50'}`}>
              <List className="w-3 h-3" />Outline
            </button>
          </div>}
          {mode === 'diagram' && (
            <div className="inline-flex items-center gap-0.5 mr-1">
              <button onClick={() => setZoom(z => Math.max(0.3, +(z - 0.2).toFixed(2)))} title="Zoom out"
                className="p-1 text-gray-500 hover:bg-gray-50 rounded"><ZoomOut className="w-3.5 h-3.5" /></button>
              <button onClick={() => setZoom(computeFit())} title="Fit to width"
                className="p-1 text-gray-500 hover:bg-gray-50 rounded"><Maximize2 className="w-3.5 h-3.5" /></button>
              <button onClick={() => setZoom(z => Math.min(2, +(z + 0.2).toFixed(2)))} title="Zoom in"
                className="p-1 text-gray-500 hover:bg-gray-50 rounded"><ZoomIn className="w-3.5 h-3.5" /></button>
              <span className="text-[11px] text-gray-400 w-9 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
            </div>
          )}
          <button onClick={exportPng} disabled={exportState === 'busy'} title="Export diagram as PNG"
            className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] border rounded hover:bg-gray-50 ${exportState === 'failed' ? 'border-red-300 text-red-600' : 'border-gray-300 text-gray-600'}`}>
            <Download className="w-3 h-3" />
            {exportState === 'busy' ? 'Exporting…' : exportState === 'failed' ? 'Export failed' : 'PNG'}
          </button>
        </div>
        <div ref={scrollRef} className="p-3 overflow-auto" style={{ maxHeight: '70vh' }}>
          {mode === 'diagram' ? (
            layout ? (
              <FlowDiagram layout={layout} accent={accentColor} selectedId={selectedNode} onSelect={setSelectedNode} zoom={zoom} onToggleCollapse={onToggleCollapse} />
            ) : layoutFailed ? (
              <div className="py-10 text-center text-sm text-gray-500">
                Couldn't load the diagram engine.
                <button onClick={() => window.location.reload()} className="ml-1 underline text-purple-600">Reload</button>
                <div className="text-xs text-gray-400 mt-1">Or switch to Outline view above.</div>
              </div>
            ) : (
              <div className="py-10 text-center text-sm text-gray-400">Laying out diagram…</div>
            )
          ) : (
            <OutlineView flow={flow} dirById={dirById} onDial={onDial} accentColor={accentColor} />
          )}
        </div>
        {/* Selection-free copy the PNG export serializes — always rendered (even
            in outline mode) so export works regardless of the active view. */}
        <div ref={diagramRef} className="hidden" aria-hidden="true">
          {layout && <FlowDiagram layout={layout} accent={accentColor} selectedId={null} onSelect={() => {}} />}
        </div>
      </div>

      <div className="border-l border-gray-200 p-3.5">
        <div className="flex items-center justify-between mb-2.5">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            {selectedKind === 'autoAttendant' ? 'Auto attendant' : selectedKind}
          </span>
          {isAdmin && onEditLocation && (
            <button onClick={() => onEditLocation(flow)} title="Edit location"
              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] border border-gray-300 rounded hover:bg-gray-50 text-gray-700">
              <Pencil className="w-3 h-3" />Edit
            </button>
          )}
        </div>
        <NodeSettings flow={flow} nodeId={selectedNode} dirById={dirById} onDial={onDial} accentColor={accentColor} />
      </div>
    </div>
  );
}
